// pages/RegisterPage.jsx
// Page d'inscription

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LONGUEUR_MIN_MOT_DE_PASSE } from '../config/motDePasse';
import { BookOpen } from 'lucide-react';
import InscriptionWp from '../components/InscriptionWp';
import './AuthPages.css';
import ChampMotDePasse from '../components/ChampMotDePasse';

function InscriptionClassique() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.mdpNeCorrespondentPas'));
      return;
    }

    if (Array.from(password).length < LONGUEUR_MIN_MOT_DE_PASSE) {
      setError(t('auth.mdpTropCourt', { min: LONGUEUR_MIN_MOT_DE_PASSE }));
      return;
    }

    setIsSubmitting(true);

    try {
      await register(email, password, nom, prenom);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <BookOpen size={40} color="#4F46E5" />
          <h1>VocaVerb</h1>
          <p>{t('auth.registerSubtitle')}</p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="prenom">{t('auth.prenom')}</label>
              <input
                id="prenom"
                type="text"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                placeholder={t('auth.prenom')}
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="nom">{t('auth.nom')}</label>
              <input
                id="nom"
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder={t('auth.nom')}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.emailRequired')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.passwordRequired')}</label>
            <ChampMotDePasse
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholderMin', { min: LONGUEUR_MIN_MOT_DE_PASSE })}
              required
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <ChampMotDePasse
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('auth.confirmPasswordPlaceholder')}
              required
              autoComplete="new-password"
            />
          </div>

          <button type="submit" className="auth-submit" disabled={isSubmitting}>
            {isSubmitting ? t('auth.creation') : t('auth.creerCompte')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('auth.dejaUnCompte')} <Link to="/login">{t('auth.seConnecter')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// Réglage WP_INSCRIPTION_ACTIVE (serveur) : tant qu'il est absent, l'ancien formulaire reste seul en service
export default function RegisterPage() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [panne, setPanne] = useState(false);

  useEffect(() => {
    fetch('/api/auth/inscription-config')
      .then((rep) => (rep.ok ? rep.json() : Promise.reject(new Error('config'))))
      .then(setConfig)
      .catch(() => setPanne(true));
  }, []);

  if (panne) return <div className="auth-page"><div className="auth-card"><div className="auth-error">{t('auth.serviceIndisponible')}</div></div></div>;
  if (!config) return <div className="auth-page"><div className="auth-card" /></div>;
  return config.actif ? <InscriptionWp turnstileSiteKey={config.turnstileSiteKey} /> : <InscriptionClassique />;
}
